import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import type { SafetyPortfolioSiteCoverage } from '../../services/reportsApi';
import { formatSiteDateTime } from '../../domain/siteTime/siteTime';
import {
  buildSiteComparisonRows,
  portfolioSiteComparisonEmptyMessage,
  sortSiteComparisonRows,
  type SiteComparisonSortDirection,
  type SiteComparisonSortKey,
} from '../../domain/safetyCases/workspacePortfolioSiteComparison';
import { portfolioOwnershipMetadataConfigured } from '../../domain/safetyCases/workspacePortfolioOwnership';

function formatFreshness(value?: string | null, timeZone?: string | null, siteLabel?: string | null): string {
  if (!value) return '—';
  const formatted = formatSiteDateTime(value, timeZone, {
    includeYear: false,
    includeTimeZoneName: Boolean(timeZone),
  });
  if (!formatted) return '—';
  if (timeZone && siteLabel) return `${formatted} · ${siteLabel}`;
  return formatted;
}

function formatCount(value: number | null): string {
  return value === null ? '—' : String(value);
}

function dataStateChipColor(dataState: string): 'success' | 'warning' | 'default' {
  if (dataState === 'loaded') return 'success';
  if (dataState === 'permission_denied' || dataState === 'unavailable') return 'warning';
  return 'default';
}

function SortableHeader({
  label,
  sortKey,
  activeKey,
  direction,
  onSort,
}: {
  label: string;
  sortKey: SiteComparisonSortKey;
  activeKey: SiteComparisonSortKey;
  direction: SiteComparisonSortDirection;
  onSort: (key: SiteComparisonSortKey) => void;
}) {
  return (
    <TableCell sortDirection={activeKey === sortKey ? direction : false}>
      <TableSortLabel
        active={activeKey === sortKey}
        direction={activeKey === sortKey ? direction : 'asc'}
        onClick={() => onSort(sortKey)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );
}

export function WorkspaceSiteComparisonPanel({
  siteCoverage,
  loading,
  onOpenSite,
}: {
  siteCoverage?: SafetyPortfolioSiteCoverage[];
  loading?: boolean;
  onOpenSite: (siteId: string) => void;
}) {
  const [sortKey, setSortKey] = useState<SiteComparisonSortKey>('site');
  const [sortDirection, setSortDirection] = useState<SiteComparisonSortDirection>('asc');
  const emptyMessage = portfolioSiteComparisonEmptyMessage(siteCoverage);
  const ownershipConfigured = portfolioOwnershipMetadataConfigured(siteCoverage);
  const rows = useMemo(
    () => sortSiteComparisonRows(buildSiteComparisonRows(siteCoverage), sortKey, sortDirection),
    [siteCoverage, sortDirection, sortKey],
  );

  const handleSort = (nextKey: SiteComparisonSortKey) => {
    if (sortKey === nextKey) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(nextKey);
    setSortDirection(nextKey === 'site' || nextKey === 'data_state' || nextKey === 'region' || nextKey === 'site_owner' ? 'asc' : 'desc');
  };

  return (
    <Stack spacing={1.5} data-testid="workspace-site-comparison">
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Site comparison
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Operational workload, ownership context, and data-state per authorized site. This is not a risk score, danger score, ranking, or safety-performance conclusion.
        </Typography>
      </Box>

      {!ownershipConfigured && !emptyMessage ? (
        <Alert severity="info" data-testid="workspace-site-comparison-ownership-note">
          Regional and site-owner labels are not configured for these sites. Work owner assignment still appears in Attention.
        </Alert>
      ) : null}

      {emptyMessage ? (
        <Alert severity="info" data-testid="workspace-site-comparison-empty">
          {emptyMessage}
        </Alert>
      ) : null}

      <TableContainer>
        <Table size="small" aria-label="Authorized site comparison">
          <TableHead>
            <TableRow>
              <SortableHeader label="Site" sortKey="site" activeKey={sortKey} direction={sortDirection} onSort={handleSort} />
              <SortableHeader label="Region" sortKey="region" activeKey={sortKey} direction={sortDirection} onSort={handleSort} />
              <SortableHeader label="Site owner" sortKey="site_owner" activeKey={sortKey} direction={sortDirection} onSort={handleSort} />
              <SortableHeader label="Data state" sortKey="data_state" activeKey={sortKey} direction={sortDirection} onSort={handleSort} />
              <SortableHeader label="Workflows loaded" sortKey="workflows" activeKey={sortKey} direction={sortDirection} onSort={handleSort} />
              <SortableHeader label="Attention items" sortKey="attention" activeKey={sortKey} direction={sortDirection} onSort={handleSort} />
              <SortableHeader label="Last updated" sortKey="freshness" activeKey={sortKey} direction={sortDirection} onSort={handleSort} />
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 2 }).map((_, index) => (
                <TableRow key={`comparison-skeleton-${index}`}>
                  {Array.from({ length: 8 }).map((__, cellIndex) => (
                    <TableCell key={`comparison-skeleton-${index}-${cellIndex}`}>
                      <Skeleton />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : rows.map((row) => (
              <TableRow key={row.siteId || row.displayName} data-testid={`workspace-site-comparison-row-${row.siteId || 'unknown'}`}>
                <TableCell>
                  <Stack spacing={0.25}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {row.displayName}
                    </Typography>
                    {row.siteId ? (
                      <Typography variant="caption" color="text.secondary">
                        {row.siteId}
                      </Typography>
                    ) : null}
                  </Stack>
                </TableCell>
                <TableCell>{row.regionLabel}</TableCell>
                <TableCell>{row.siteOwner}</TableCell>
                <TableCell>
                  <Stack spacing={0.5}>
                    <Chip
                      size="small"
                      label={row.dataStateLabel}
                      color={dataStateChipColor(row.dataState)}
                      variant="outlined"
                    />
                    {row.stateDetail ? (
                      <Typography variant="caption" color="text.secondary">
                        {row.stateDetail}
                      </Typography>
                    ) : null}
                  </Stack>
                </TableCell>
                <TableCell>{formatCount(row.workflowCount)}</TableCell>
                <TableCell>{formatCount(row.attentionCount)}</TableCell>
                <TableCell>{formatFreshness(row.freshnessAt, row.timezone, row.displayName)}</TableCell>
                <TableCell align="right">
                  {row.canOpenSite ? (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => onOpenSite(row.siteId)}
                      data-testid={`workspace-site-comparison-open-${row.siteId}`}
                    >
                      Open site
                    </Button>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      Not available
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}
